/**
 * Two-Factor Authentication Setup Modal
 *
 * Standalone 3-step modal for enabling 2FA. Can be opened from
 * SecuritySettings or from notification panel (in-context action).
 *
 * Steps: method-select → send-code → verify
 *
 * @module features/user-dashboard/components/settings/TwoFactorSetupModal
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Mail,
  Phone,
  Smartphone,
  CheckCircle,
  RefreshCw,
  AlertCircle,
  ChevronLeft,
} from 'lucide-react';
import { useSecurity } from '../../../../entities/user/hooks/useSecurity';
import { useProfile } from '../../../../entities/user/hooks/useProfile';

// ── Types ────────────────────────────────────────────────────────────

interface TwoFactorSetupModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Called when user closes the modal */
  onClose: () => void;
  /** Called after 2FA is successfully enabled */
  onSuccess: () => void;
}

type TwoFactorMethod = 'email' | 'phone';
type SetupStep = 'method-select' | 'send-code' | 'verify';

// ── OTP Input Component ──────────────────────────────────────────────

const OTP_LENGTH = 6;

interface OtpInputProps {
  code: string[];
  onChange: (code: string[]) => void;
  disabled?: boolean;
}

/** 6-digit OTP input with auto-focus, paste support, and backspace navigation */
const OtpInput: React.FC<OtpInputProps> = ({ code, onChange, disabled }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/[^0-9]/g, '');
    if (digit.length > 1) return;

    const newCode = [...code];
    newCode[index] = digit;
    onChange(newCode);

    // Auto-focus next input
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    if (pasted.length === OTP_LENGTH) {
      onChange(pasted.split(''));
      inputRefs.current[OTP_LENGTH - 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center">
      {/* First 3 digits */}
      <div className="flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={code[i]}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            disabled={disabled}
            className="w-11 h-11 sm:w-12 sm:h-12 text-center text-base sm:text-lg font-semibold bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-hidden transition-all disabled:opacity-50"
            aria-label={`Digit ${i + 1}`}
          />
        ))}
      </div>

      {/* Separator dot */}
      <div className="flex items-center px-1">
        <div className="w-1.5 h-1.5 bg-gray-300 rounded-full" />
      </div>

      {/* Last 3 digits */}
      <div className="flex gap-1.5">
        {[3, 4, 5].map((i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={code[i]}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            disabled={disabled}
            className="w-11 h-11 sm:w-12 sm:h-12 text-center text-base sm:text-lg font-semibold bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-hidden transition-all disabled:opacity-50"
            aria-label={`Digit ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// ── Step: Method Select ──────────────────────────────────────────────

interface MethodSelectStepProps {
  onSelect: (method: TwoFactorMethod) => void;
  onClose: () => void;
  hasPhone: boolean;
}

const MethodSelectStep: React.FC<MethodSelectStepProps> = ({ onSelect, onClose, hasPhone }) => (
  <div className="space-y-5">
    <div className="text-center space-y-2">
      <div className="mx-auto w-12 h-12 bg-linear-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
        <Smartphone className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
        Enable Two-Factor Authentication
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Choose how you want to receive verification codes
      </p>
    </div>

    <div className="space-y-3">
      <button
        onClick={() => onSelect('email')}
        className="w-full flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all"
      >
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
          <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-medium text-gray-900 dark:text-white text-sm">Email</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Receive codes via email</div>
        </div>
      </button>

      <button
        onClick={() => hasPhone ? onSelect('phone') : undefined}
        disabled={!hasPhone}
        className="w-full flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:bg-gray-50"
      >
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
          <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-medium text-gray-900 dark:text-white text-sm">Phone (SMS)</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {hasPhone ? 'Receive codes via SMS' : 'Add a phone number first'}
          </div>
        </div>
        {!hasPhone && (
          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
        )}
      </button>
    </div>

    <button
      onClick={onClose}
      className="w-full px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      Not now
    </button>
  </div>
);

// ── Step: Send Code ──────────────────────────────────────────────────

interface SendCodeStepProps {
  method: TwoFactorMethod;
  contactInfo: string;
  onSend: () => void;
  onBack: () => void;
  isSending: boolean;
}

const SendCodeStep: React.FC<SendCodeStepProps> = ({
  method,
  contactInfo,
  onSend,
  onBack,
  isSending,
}) => (
  <div className="space-y-5">
    <div className="text-center space-y-2">
      <div className="mx-auto w-12 h-12 bg-linear-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
        {method === 'email' ? (
          <Mail className="w-6 h-6 text-white" />
        ) : (
          <Phone className="w-6 h-6 text-white" />
        )}
      </div>
      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
        Confirm {method === 'email' ? 'Email' : 'Phone'} Verification
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        We'll send a 6-digit code to verify your {method === 'email' ? 'email address' : 'phone number'}
      </p>
    </div>

    {/* Contact info card */}
    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
      <div className="flex items-center gap-3">
        {method === 'email' ? (
          <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        ) : (
          <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {method === 'email' ? 'Email Address' : 'Phone Number'}
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {contactInfo}
          </div>
        </div>
      </div>
    </div>

    <div className="flex gap-3">
      <button
        onClick={onBack}
        disabled={isSending}
        className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>
      <button
        onClick={onSend}
        disabled={isSending}
        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-linear-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSending ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span>Sending code...</span>
          </>
        ) : (
          <>
            <CheckCircle className="w-4 h-4" />
            <span>Send verification code</span>
          </>
        )}
      </button>
    </div>
  </div>
);

// ── Step: Verify Code ────────────────────────────────────────────────

interface VerifyStepProps {
  method: TwoFactorMethod;
  contactInfo: string;
  code: string[];
  onCodeChange: (code: string[]) => void;
  onVerify: () => void;
  onResend: () => void;
  onClose: () => void;
  timer: number;
  isVerifying: boolean;
  isResending: boolean;
}

const VerifyStep: React.FC<VerifyStepProps> = ({
  method,
  contactInfo,
  code,
  onCodeChange,
  onVerify,
  onResend,
  onClose,
  timer,
  isVerifying,
  isResending,
}) => {
  const isCodeComplete = code.join('').length === OTP_LENGTH;

  return (
    <div className="space-y-5">
      <div className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 bg-linear-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
          Verify Your {method === 'email' ? 'Email' : 'Phone'}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Enter the 6-digit code sent to your {method === 'email' ? 'email' : 'phone'}
        </p>
      </div>

      {/* Contact info */}
      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
        <div className="flex items-center gap-3">
          {method === 'email' ? (
            <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          ) : (
            <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {method === 'email' ? 'Email Address' : 'Phone Number'}
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {contactInfo}
            </div>
          </div>
        </div>
      </div>

      {/* OTP input */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
          Enter 6-digit code
        </label>
        <OtpInput code={code} onChange={onCodeChange} disabled={isVerifying} />
      </div>

      {/* Timer / Resend */}
      <div className="text-center">
        {timer > 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Resend code in{' '}
            <span className="font-mono font-bold text-blue-600 dark:text-blue-400">
              {timer}s
            </span>
          </p>
        ) : (
          <button
            type="button"
            onClick={onResend}
            disabled={isResending}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResending ? 'Resending...' : "Didn't receive code? Resend"}
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium text-sm"
        >
          Cancel
        </button>
        <button
          onClick={onVerify}
          disabled={!isCodeComplete || isVerifying}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-linear-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
        >
          {isVerifying ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Verifying...</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>Verify & Enable</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ── Main Modal ───────────────────────────────────────────────────────

/**
 * TwoFactorSetupModal -- self-contained 2FA enable flow.
 *
 * Uses `useSecurity()` for API calls and `useProfile()` for
 * email/phone data. Designed to be mountable from any context
 * (settings page, notification panel, onboarding).
 */
export const TwoFactorSetupModal: React.FC<TwoFactorSetupModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const security = useSecurity();
  const profileQuery = useProfile();
  const profile = profileQuery.data;

  const [step, setStep] = useState<SetupStep>('method-select');
  const [method, setMethod] = useState<TwoFactorMethod | null>(null);
  const [code, setCode] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [timer, setTimer] = useState(60);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('method-select');
      setMethod(null);
      setCode(Array(OTP_LENGTH).fill(''));
      setTimer(60);
    }
  }, [isOpen]);

  // Countdown timer for resend
  useEffect(() => {
    if (step !== 'verify' || timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((t) => t - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [step, timer]);

  const userEmail = profile?.data?.email ?? '';
  const userPhone = profile?.data?.phoneNumber ?? '';
  const hasPhone = Boolean(userPhone);

  const contactInfo = method === 'email' ? userEmail : userPhone;

  /** Step 1 → Step 2: User picks a method */
  const handleMethodSelect = useCallback((selected: TwoFactorMethod) => {
    setMethod(selected);
    setStep('send-code');
  }, []);

  /** Step 2 → Step 3: Send the verification code */
  const handleSendCode = useCallback(async () => {
    if (!method) return;

    try {
      await security.enableTwoFactor.mutateAsync({
        method: method === 'phone' ? 'sms' : 'email',
      });

      setCode(Array(OTP_LENGTH).fill(''));
      setTimer(60);
      setStep('verify');
    } catch (error) {
      console.error('Failed to send 2FA code:', error);
    }
  }, [method, security.enableTwoFactor]);

  /** Step 3: Verify the OTP code */
  const handleVerify = useCallback(async () => {
    const codeString = code.join('');
    if (codeString.length !== OTP_LENGTH) return;

    try {
      await security.verifyTwoFactor.mutateAsync({ code: codeString });
      onSuccess();
    } catch (error) {
      console.error('Failed to verify 2FA code:', error);
    }
  }, [code, security.verifyTwoFactor, onSuccess]);

  /** Resend the verification code */
  const handleResend = useCallback(async () => {
    try {
      await security.resendTwoFactorCode.mutateAsync();
      setTimer(60);
    } catch (error) {
      console.error('Failed to resend 2FA code:', error);
    }
  }, [security.resendTwoFactorCode]);

  /** Go back to method selection */
  const handleBack = useCallback(() => {
    setStep('method-select');
    setMethod(null);
  }, []);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-3 top-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
            aria-label="Close modal"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>

          {/* Content */}
          <div className="p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.15 }}
              >
                {step === 'method-select' && (
                  <MethodSelectStep
                    onSelect={handleMethodSelect}
                    onClose={onClose}
                    hasPhone={hasPhone}
                  />
                )}

                {step === 'send-code' && method && (
                  <SendCodeStep
                    method={method}
                    contactInfo={contactInfo}
                    onSend={handleSendCode}
                    onBack={handleBack}
                    isSending={security.isEnabling2FA}
                  />
                )}

                {step === 'verify' && method && (
                  <VerifyStep
                    method={method}
                    contactInfo={contactInfo}
                    code={code}
                    onCodeChange={setCode}
                    onVerify={handleVerify}
                    onResend={handleResend}
                    onClose={onClose}
                    timer={timer}
                    isVerifying={security.isVerifying2FA}
                    isResending={security.isResendingCode}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TwoFactorSetupModal;
